import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { ImportSession } from '../types';
import { importsApi } from '../api/imports';
import { useAuth } from './AuthContext';

interface ImportContextType {
  currentSession: ImportSession | null;
  loading: boolean;
  refreshSession: () => Promise<void>;
  updateSession: (session: ImportSession) => void;
  clearSession: () => void;
}

const ImportContext = createContext<ImportContextType | undefined>(undefined);

export const useImport = () => {
  const context = useContext(ImportContext);
  if (!context) {
    throw new Error('useImport must be used within an ImportProvider');
  }
  return context;
};

interface ImportProviderProps {
  children: ReactNode;
}

export const ImportProvider: React.FC<ImportProviderProps> = ({ children }) => {
  const [currentSession, setCurrentSession] = useState<ImportSession | null>(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const refreshSession = useCallback(async () => {
    if (!user) {
      setCurrentSession(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const session = await importsApi.getCurrentSession();
      setCurrentSession(session);
    } catch (err) {
      console.error('Failed to fetch import session:', err);
      setCurrentSession(null);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const updateSession = (session: ImportSession) => {
    setCurrentSession(session);
  };

  const clearSession = () => {
    setCurrentSession(null);
  };

  useEffect(() => {
    refreshSession();
  }, [user, refreshSession]);

  return (
    <ImportContext.Provider value={{
      currentSession,
      loading,
      refreshSession,
      updateSession,
      clearSession,
    }}>
      {children}
    </ImportContext.Provider>
  );
};